import { AsyncPipe } from "@angular/common";
import {
    Component,
    Input,
    OnDestroy,
    OnInit,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { ActionButtonComponent } from "@components/action-button/action-button.component";
import { BlockComponent } from "@components/container-block/block.component";
import { HeaderComponent } from "@components/header/header.component";
import { TabComponent } from "@components/tabs/tab/tab.component";
import { TabsComponent } from "@components/tabs/tabs.component";
import { Observable, Subject, SubscriptionLike } from "rxjs";
import { map } from "rxjs/operators";
import { PermittedDirective } from "~app/common/directives/permitted/permitted.directive";
import { IVirkeskopKontraktEntityDTO } from "~app/common/models/entity-models";
import { GuidUtils } from "~app/common/utils/guid-utils";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { Avtal } from "~app/features/virkeskop/models/avtal-models";
import { AvtalMediatorService } from "~app/features/virkeskop/services/avtal-mediator.service";
import { AvtalService } from "~app/features/virkeskop/services/avtal.service";
import { VirkeskopValidationService } from "../../services/validation/virkeskop-validation.service";
import { KontraktItemComponent } from "../kontrakt-item/kontrakt-item.component";

@Component({
    selector: "rask-kontrakt-list",
    templateUrl: "./kontrakt-list.component.html",
    styleUrls: ["./kontrakt-list.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        BlockComponent,
        HeaderComponent,
        ActionButtonComponent,
        TabsComponent,
        TabComponent,
        KontraktItemComponent,
        AsyncPipe,
        PermittedDirective
    ]
})
export class KontraktListComponent implements OnInit, OnDestroy {
    @Input() isSkogsvardFPV = false;

    protected avtal: Avtal;
    protected kontrakt: IVirkeskopKontraktEntityDTO[] = [];
    protected selectedTabId?: string;
    protected selectedTabId$: Observable<string>;

    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly avtalMediatorService = inject(AvtalMediatorService);
    private readonly avtalService = inject(AvtalService);
    private readonly ngUnsubscribe$ = new Subject<void>();
    private readonly subscriptions: SubscriptionLike[] = [];
    private readonly validationService = inject(VirkeskopValidationService);

    getIcon(kontrakt: IVirkeskopKontraktEntityDTO): string {
        return this.validationService.getEntitySeverity("kontrakt", kontrakt.guid);
    }

    loaded($event, tab): void {
        tab.spinner = $event;
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
        this.subscriptions.forEach(sub => sub.unsubscribe());
        this.subscriptions.splice(0);
    }

    ngOnInit(): void {
        const avtalSubject = this.avtalMediatorService.avtal$;
        const avtalSubscription = avtalSubject.subscribe(co => {
            this.avtal = co?.current;

            if (this.avtal?.id) {
                this.avtalService.getKontraktForAvtal(this.avtal.id).subscribe({
                    next: kontrakt => {
                        this.kontrakt = kontrakt;
                    },
                    error: () => {
                        // inga kontrakt finns
                    }
                });
            } else {
                this.kontrakt = [];
            }
        });
        const kontraktCreatedSubject = this.avtalMediatorService.kontraktCreated$;
        const kontraktCreatedSubscription = kontraktCreatedSubject.subscribe(k => {
            if (k != null) {
                this.createKontrakt(k.benamning, k.guid);
            }
        });

        this.selectedTabId$ = this.activatedRoute.queryParams.pipe(
            map(params => params.kontraktId)
        );

        this.subscriptions.push(avtalSubscription);
        this.subscriptions.push(kontraktCreatedSubscription);
    }

    trackById(_index: number, item: IVirkeskopKontraktEntityDTO) {
        return item.guid;
    }

    protected clickCreateKontrakt(): void {
        this.createKontrakt("", GuidUtils.newGuid());
    }

    protected clickCreateSkogsvardsKontrakt(): void {
        this.createSkogsvardsKontrakt("", GuidUtils.newGuid());
    }

    protected getTabName(kontrakt: IVirkeskopKontraktEntityDTO): string {
        return ObjectUtils.isNullOrUndefined(kontrakt.name) || kontrakt.name === "" ? "Nytt" : kontrakt.name;
    }

    protected tabSelected(kontraktEntity: IVirkeskopKontraktEntityDTO): void {
        this.avtalMediatorService.setActiveKontrakt(kontraktEntity);
    }

    private createKontrakt(benamning: string, guid?: string) {
        const kontrakt: IVirkeskopKontraktEntityDTO = {
            id: null,
            name: benamning,
            guid: guid,
            type: "virkeskopkontrakt",
            skogsvard: false
        };

        this.kontrakt.unshift(kontrakt);
    }

    private createSkogsvardsKontrakt(benamning: string, guid?: string) {
        const kontrakt: IVirkeskopKontraktEntityDTO = {
            id: null,
            name: benamning,
            guid: guid,
            type: "virkeskopkontrakt"
        };

        if (this.isSkogsvardFPV) {
            kontrakt.skogsvardFPV = true;
        } else {
            kontrakt.skogsvard = true;
        }

        this.kontrakt.unshift(kontrakt);
    }
}
